import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  Rating,
  TextField,
  Typography,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Custom styled component for file uploads
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
const grayTheme = createTheme({
  palette: {
    primary: {
      main: '#616161', // Gray
    },
  },
});

const platforms = ['Meesho', 'Flipkart', 'Amazon', 'Shopsy', 'Myntra'];
const colors = ['Red', 'Blue', 'Black', 'White', 'Green', 'Yellow', 'Pink', 'Purple', 'Orange', 'Gray', 'Multi-color'];

const ProductReviewForm = () => {
  const [formData, setFormData] = useState({
    reviewerId: '',
    productName: '',
    category: '',
    platform: '',
    rating: 0,
    color: '',
    gender: '',
    price: '',
    description: '',
    link: '',
    images: [],
    video: null,
    subCategory: '',
    productMainImage: null
  });
  const [categoriesList, setCategoriesList] = useState([]);
  const [subCategoriesList, setSubCategoriesList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    axios.get('https://reviewhub-lc.onrender.com/api/categories')
      .then(res => setCategoriesList(res.data))
      .catch(err => console.error('Error fetching categories:', err));
  }, []);

  useEffect(() => {
    if (formData.category && formData.gender) {
      const selectedCategory = categoriesList.find(c => c.categoryName === formData.category);
      if (!selectedCategory) return;

      axios.get('https://reviewhub-lc.onrender.com/api/subcategories/filter', {
        params: {
          categoryId: selectedCategory._id,
          gender: formData.gender
        }
      })
        .then(res => setSubCategoriesList(res.data))
        .catch(err => console.error('Error fetching subcategories:', err));
    } else {
      setSubCategoriesList([]);
    }
  }, [formData.category, formData.gender, categoriesList]);

  useEffect(() => {
    const storedResellerId = localStorage.getItem('resellerId');
    if (storedResellerId) {
      setFormData((prev) => ({ ...prev, reviewerId: storedResellerId }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...Array.from(files)],
      }));
    } else if (name === 'productMainImage') {
      setFormData((prev) => ({
        ...prev,
        productMainImage: files[0],
      }));
    } else if (name === 'video') {
      setFormData((prev) => ({
        ...prev,
        video: files[0],
      }));
    } else {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]: value,
        };
        if (name === 'category' || name === 'gender') {
          updated.subCategory = '';
        }
        return updated;
      });
    }
  };

  const handleRatingChange = (_, value) => {
    setFormData((prev) => ({ ...prev, rating: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    for (const key in formData) {
      if (key === 'images') {
        formData.images.forEach((img) => data.append('images', img));
      } else if (formData[key] !== null) {
        data.append(key, formData[key]);
      }
    }
    try {
      const response = await fetch('https://reviewhub-lc.onrender.com/api/reviews/submit-review', {
        method: 'POST',
        body: data
      });
      const result = await response.json();
      if (response.ok) {
        alert(result.message || 'Review submitted successfully! 🎉');
        setFormData({
          reviewerId: '',
          productName: '',
          category: '',
          platform: '',
          rating: 0,
          color: '',
          gender: '',
          price: '',
          description: '',
          link: '',
          images: [],
          video: null,
          subCategory: '',
          productMainImage: null
        });
      } else {
        alert(result.message || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('An error occurred while submitting the review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
        <ThemeProvider theme={grayTheme}>
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Paper elevation={6} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 3 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          align="center"
          sx={{ mb: 4 }}
        >
          Submit Your Product Review 
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={4}>

            {/* Product Details Section */}
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
                Product Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Platform"
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                      sx={{ minWidth: 240 }}  
                    MenuProps={{ PaperProps: { sx: { width: '250px' } } }}
                  >
                    {platforms.map((plat) => (
                      <MenuItem key={plat} value={plat}>{plat}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                      sx={{ minWidth: 240 }}  
                    MenuProps={{ PaperProps: { sx: { width: '250px' } } }}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Kids">Kids</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                      sx={{ minWidth: 240 }}  
                    MenuProps={{ PaperProps: { sx: { width: '250px' } } }}
                  >
                    {categoriesList.map((cat) => (
                      <MenuItem key={cat._id} value={cat.categoryName}>
                        {cat.categoryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Sub Category"
                    name="subCategory"
                    value={formData.subCategory}
                    onChange={handleChange}
                      sx={{ minWidth: 240 }}  
                    disabled={!formData.category || !formData.gender}
                    MenuProps={{ PaperProps: { sx: { width: '250px' } } }}
                  >
                    {subCategoriesList.map((sub) => (
                      <MenuItem key={sub._id} value={sub.subCategoryName}>
                        {sub.subCategoryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Product Name"
                    name="productName"
                    value={formData.productName}
                          sx={{ minWidth: 240 }}  
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Price"
                    name="price"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.price}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                      sx={{ minWidth: 240 }}  
                    MenuProps={{ PaperProps: { sx: { width: '250px' } } }}
                  >
                    {colors.map((color) => (
                      <MenuItem key={color} value={color}>{color}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Product Link (URL)"
                    name="link"
                    type="url"
                    value={formData.link}
                          sx={{ minWidth: 240 }}  
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Review Details Section */}
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
                Your Review
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Reviewer ID"
                    name="reviewerId"
                    value={formData.reviewerId}
                    onChange={handleChange}
                          sx={{ minWidth: 240 }}  
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography gutterBottom sx={{ fontWeight: 'bold' }}>Rating *</Typography>
                  <Rating
                    name="rating"
                    value={formData.rating}
                    onChange={handleRatingChange}
                    size="large"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    label="Review Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    helperText="Please provide a detailed description of your experience with the product."
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Media Upload Section */}
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
                Upload Media
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      startIcon={<ImageIcon />}
                      sx={{ height: 56 }}
                    >
                      Upload Main Image *
                      <VisuallyHiddenInput
                        type="file"
                        name="productMainImage"
                        accept="image/*"
                        onChange={handleChange}
                      />
                    </Button>
                    {formData.productMainImage && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {formData.productMainImage.name}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      (The main image for your review)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      startIcon={<ImageIcon />}
                      sx={{ height: 56 }}
                    >
                      Upload Additional Images
                      <VisuallyHiddenInput
                        type="file"
                        name="images"
                        accept="image/*"
                        multiple
                        onChange={handleChange}
                      />
                    </Button>
                    {formData.images.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {formData.images.length} file(s) selected
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      (Upload multiple images to showcase the product)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      startIcon={<VideoLibraryIcon />}
                      sx={{ height: 56 }}
                    >
                      Upload Unboxing/Demo Video
                      <VisuallyHiddenInput
                        type="file"
                        name="video"
                        accept="video/*"
                        onChange={handleChange}
                      />
                    </Button>
                    {formData.video && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {formData.video.name}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      (Optional: Add a video to your review)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  mt: 4,
                  py: 1.5,
                  backgroundColor: '#1a242f',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#334455' },
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </Grid>

          </Grid>
        </Box>
      </Paper>
    </Container>
    </ThemeProvider>
  );
};

export default ProductReviewForm;